import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import type {
  UpdateVisitorCustomFieldConfigStatusMutation,
  UpdateVisitorCustomFieldConfigStatusMutationVariables,
} from 'graphql-query-contracts';
import { updateVisitorCustomFieldConfigStatus } from 'graphql-queries';
import type { CustomFieldDetailModel } from './types';
import {
  MAX_ENABLED_FIELDS_REACHED_KEY,
  NO_ORGANIZATION_KEY,
  runUpdateVisitorCustomFieldStatus,
} from './customFieldsTabModelUtils';
import { CUSTOM_FIELD_ENABLED_PER_ORG_MAX } from '../../../../customFieldLimits';

export function useCustomFieldStatusUpdate({
  organizationId,
  customFieldConfigs,
}: {
  organizationId: string | null;
  customFieldConfigs: ReadonlyArray<{ isEnabled: boolean }> | null | undefined;
}) {
  const { t } = useTranslation();
  const [pendingDisableRow, setPendingDisableRow] = useState<CustomFieldDetailModel | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // Synchronous guard against double-click races: setIsLoading(true) only
  // commits on the next render, so two clicks fired in the same tick would
  // both see isLoading === false and both kick off mutations. A ref flips
  // synchronously and closes that window.
  const inFlightRef = useRef(false);

  const [updateFieldStatus] = useMutation<
    UpdateVisitorCustomFieldConfigStatusMutation,
    UpdateVisitorCustomFieldConfigStatusMutationVariables
  >(updateVisitorCustomFieldConfigStatus, {
    errorPolicy: 'all',
    update(cache, { data }) {
      const updatedConfig = data?.updateVisitorCustomFieldConfigStatus;

      if (!updatedConfig) return;

      cache.modify({
        id: cache.identify(updatedConfig),
        fields: {
          isEnabled: () => updatedConfig.isEnabled,
          updatedAtDatetime: () => updatedConfig.updatedAtDatetime,
        },
      });
    },
  });

  const enabledFieldCount = useMemo(
    () => (customFieldConfigs ?? []).filter(c => c.isEnabled).length,
    [customFieldConfigs]
  );

  const showToast = useCallback((msg: string) => window.Toast.show(msg), []);

  const executeStatusUpdate = useCallback(
    async (row: CustomFieldDetailModel, isEnabled: boolean): Promise<boolean> => {
      if (inFlightRef.current) return false;

      const orgId = row.organizationId || organizationId;

      if (!orgId) {
        showToast(t(NO_ORGANIZATION_KEY));

        return false;
      }

      inFlightRef.current = true;
      setIsLoading(true);

      try {
        return await runUpdateVisitorCustomFieldStatus({
          updateFieldStatus,
          showToast,
          t,
          orgId,
          id: row.id,
          isEnabled,
        });
      } finally {
        inFlightRef.current = false;
        setIsLoading(false);
      }
    },
    [organizationId, showToast, t, updateFieldStatus]
  );

  const handleToggleStatus = useCallback(
    (row: CustomFieldDetailModel): Promise<boolean> => {
      if (row.isEnabled) {
        setPendingDisableRow(row);

        return Promise.resolve(false);
      }

      if (enabledFieldCount >= CUSTOM_FIELD_ENABLED_PER_ORG_MAX) {
        showToast(t(MAX_ENABLED_FIELDS_REACHED_KEY, { maxEnabled: CUSTOM_FIELD_ENABLED_PER_ORG_MAX }));

        return Promise.resolve(false);
      }

      return executeStatusUpdate(row, true);
    },
    [enabledFieldCount, executeStatusUpdate, showToast, t]
  );

  // Dismiss the confirmation dialog before the mutation starts so the loading
  // indicator surfaces on the list (not the modal), per UX expectation.
  const handleDisableConfirm = useCallback(async (): Promise<void> => {
    const row = pendingDisableRow;

    if (!row) return;

    setPendingDisableRow(null);
    await executeStatusUpdate(row, false);
  }, [pendingDisableRow, executeStatusUpdate]);

  const handleDisableCancel = useCallback((): void => {
    setPendingDisableRow(null);
  }, []);

  return {
    handleToggleStatus,
    handleDisableConfirm,
    handleDisableCancel,
    pendingDisableRow,
    isLoading,
  };
}
